/** @format */

import { doc, getDoc, setDoc, updateDoc, increment } from "firebase/firestore";
import { db } from "../firebase/config";

// Counter document structure for tracking collection sizes
const COUNTER_COLLECTION = "collectionCounters";

/**
 * Get the current count for a collection
 * @param {string} collectionName - Name of the collection to count
 * @returns {Promise<number>} - Current count
 */
export const getCollectionCount = async (collectionName) => {
  try {
    const counterRef = doc(db, COUNTER_COLLECTION, collectionName);
    const counterDoc = await getDoc(counterRef);

    if (counterDoc.exists()) {
      return counterDoc.data().count || 0;
    }

    // If counter doesn't exist, create it with count 0
    await setDoc(counterRef, {
      count: 0,
      lastUpdated: new Date().toISOString(),
    });
    return 0;
  } catch (error) {
    console.error(`Error getting count for ${collectionName}:`, error);
    return 0;
  }
};

/**
 * Increment collection count (call when adding documents)
 * @param {string} collectionName - Name of the collection
 * @param {number} amount - Amount to increment (default: 1)
 */
export const incrementCollectionCount = async (collectionName, amount = 1) => {
  try {
    const counterRef = doc(db, COUNTER_COLLECTION, collectionName);
    await updateDoc(counterRef, {
      count: increment(amount),
      lastUpdated: new Date().toISOString(),
    });
  } catch (error) {
    console.error(`Error incrementing count for ${collectionName}:`, error);
  }
};

/**
 * Decrement collection count (call when deleting documents)
 * @param {string} collectionName - Name of the collection
 * @param {number} amount - Amount to decrement (default: 1)
 */
export const decrementCollectionCount = async (collectionName, amount = 1) => {
  try {
    const counterRef = doc(db, COUNTER_COLLECTION, collectionName);
    await updateDoc(counterRef, {
      count: increment(-amount),
      lastUpdated: new Date().toISOString(),
    });
  } catch (error) {
    console.error(`Error decrementing count for ${collectionName}:`, error);
  }
};

/**
 * Set collection count to a specific value
 * @param {string} collectionName - Name of the collection
 * @param {number} count - New count value
 */
export const setCollectionCount = async (collectionName, count) => {
  try {
    const counterRef = doc(db, COUNTER_COLLECTION, collectionName);
    await setDoc(counterRef, {
      count: Math.max(0, count), // Ensure count is never negative
      lastUpdated: new Date().toISOString(),
    });
  } catch (error) {
    console.error(`Error setting count for ${collectionName}:`, error);
  }
};

/**
 * Initialize counters for all collections
 * Call this once to set up the counter system
 */
export const initializeCounters = async () => {
  const collections = [
    "users",
    "courses",
    "classrooms",
    "books",
    "BooksReviews",
    "categories",
    "tags",
    "pendingUsers",
    "withdrawals",
    "OfflinePayment",
    "OnlinePayment",
  ];

  try {
    for (const collectionName of collections) {
      const counterRef = doc(db, COUNTER_COLLECTION, collectionName);
      const counterDoc = await getDoc(counterRef);

      if (!counterDoc.exists()) {
        await setDoc(counterRef, {
          count: 0,
          lastUpdated: new Date().toISOString(),
          collectionName,
        });
        console.log(`✅ Initialized counter for ${collectionName}`);
      }
    }
    console.log("🎉 All collection counters initialized!");
  } catch (error) {
    console.error("Error initializing counters:", error);
  }
};

/**
 * Get all collection counts at once
 * @returns {Promise<Object>} - Object with collection names as keys and counts as values
 */
export const getAllCollectionCounts = async () => {
  try {
    const counts = {};
    const collections = [
      "users",
      "courses",
      "classrooms",
      "books",
      "BooksReviews",
      "categories",
      "tags",
      "pendingUsers",
      "withdrawals",
      "OfflinePayment",
      "OnlinePayment",
    ];

    for (const collectionName of collections) {
      counts[collectionName] = await getCollectionCount(collectionName);
    }

    return counts;
  } catch (error) {
    console.error("Error getting all collection counts:", error);
    return {};
  }
};

/**
 * Batch update multiple collection counts
 * @param {Object} updates - Object with collection names as keys and count updates as values
 */
export const batchUpdateCounts = async (updates) => {
  try {
    const promises = Object.entries(updates).map(([collectionName, count]) => {
      return setCollectionCount(collectionName, count);
    });

    await Promise.all(promises);
    console.log("✅ Batch updated collection counts");
  } catch (error) {
    console.error("Error batch updating counts:", error);
  }
};
